import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type { KeyPrefix } from 'i18next';
import { useForm } from 'react-hook-form';
import type {
  FieldValues,
  Path,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { z } from 'zod/v4';

import i18n from '@/constants/i18n';
import { translatedZodError } from '@/constants/translatedZodErrors';

import { useOnMount } from './shared/useOnMount';

export type UseTranslatedFormOptions<
  TFieldValues extends FieldValues = FieldValues,
  TContext = Record<string, string>,
  TTransformedValues = TFieldValues,
> = Omit<
  UseFormProps<TFieldValues, TContext, TTransformedValues>,
  'resolver'
> & {
  fieldsNs: KeyPrefix<'common'>;
  schema: z.ZodSchema<TFieldValues>;
};

export function useTranslatedForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext = Record<string, string>,
  TTransformedValues = TFieldValues,
>({
  fieldsNs,
  schema,
  ...options
}: UseTranslatedFormOptions<
  TFieldValues,
  TContext,
  TTransformedValues
>): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  const form = useForm<TFieldValues, TContext, TTransformedValues>({
    reValidateMode: options.reValidateMode ?? 'onChange',
    resolver: standardSchemaResolver(schema),
    ...options,
  });

  /**
   * Add translations when language changes
   * Avoiding overwritting translations on re-renders
   */
  useOnMount(() => {
    const onLanguageChanged = (lng: string) => {
      z.config(
        translatedZodError({
          tForm: i18n.getFixedT(lng, 'common', fieldsNs),
          tError: i18n.getFixedT(lng, 'errors', 'validation'),
        }),
      );

      /**
       * Revalidate form to apply new error messages when language changes
       */
      form.trigger(Object.keys(form.formState.errors) as Path<TFieldValues>[]);
    };

    z.config(
      translatedZodError({
        tForm: i18n.getFixedT(i18n.language, 'common', fieldsNs),
        tError: i18n.getFixedT(i18n.language, 'errors', 'validation'),
      }),
    );

    i18n.on('languageChanged', onLanguageChanged);
    return () => i18n.off('languageChanged', onLanguageChanged);
  });

  return form;
}
